// ============================================================================
// ROOM CHARGING — off switch and credit limit of a stay's folio (BR-FOL-008/009)
// ============================================================================
// One dialog for the three places the desk sets it: the Folio screen, the Front
// Office day sheet (in-house row) and Check-In. Room charging off stops the
// restaurant and other outlets charging the room — they collect directly — while
// room and meal nights and the desk's own charges still post. Use it for group
// rooms shared by several guests, where an outlet bill cannot be traced to one.
//
// Two modes:
//   folioId given  — saved at once through posting-control (reason, audit log)
//   onPending only — Check-In before the folio exists: the choice is handed back
//                    and applied right after the check-in commits

import React, { useEffect, useState } from 'react';
import { STORAGE_KEYS } from 'auth/storageKeys';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { folioService } from 'api/services/folioService';
import { INHOUSE_ACTIONS, INHOUSE_MODULE, type FolioDetail, type FolioPostingControlRequest } from 'api/types/folio.types';

/** posting-control is a pms-inhouse action; screens of other modules read it from the role matrix. */
export const canControlRoomCharging = (): boolean => {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEYS.RBAC_MODULE_ACTIONS) || '{}');
    return Array.isArray(all?.[INHOUSE_MODULE]) && all[INHOUSE_MODULE].includes(INHOUSE_ACTIONS.postingControl);
  } catch {
    return false;
  }
};

interface Props {
  isOpen: boolean;
  /** Shown in the title — the folio number, or the booking number before check-in. */
  label: string;
  noPost: boolean;
  creditLimit?: number | null;
  /** Save to this folio. Omit with onPending before the folio exists. */
  folioId?: string | null;
  onClose: () => void;
  onSaved?: (folio: FolioDetail) => void;
  onPending?: (request: FolioPostingControlRequest) => void;
  /** Reason already given for a pending choice, so reopening keeps it. */
  initialReason?: string;
}

const RoomChargingModal: React.FC<Props> = ({
  isOpen, label, noPost: currentNoPost, creditLimit, folioId, onClose, onSaved, onPending, initialReason,
}) => {
  const [noPost, setNoPost] = useState(false);
  const [limit, setLimit] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setNoPost(currentNoPost);
    setLimit(creditLimit != null ? String(creditLimit) : '');
    setReason(initialReason ?? '');
  }, [isOpen, currentNoPost, creditLimit, initialReason]);

  if (!isOpen) return null;

  const save = async () => {
    if (reason.trim().length < 3) return;
    const request: FolioPostingControlRequest = {
      noPost,
      creditLimit: limit.trim() === '' ? null : Number(limit),
      reason: reason.trim(),
    };
    if (!folioId) {
      onPending?.(request);
      return;
    }
    setBusy(true);
    try {
      const updated = await folioService.setPostingControl(folioId, request);
      notificationService.toast.success(noPost ? 'Room charging switched off.' : 'Room charging updated.');
      onSaved?.(updated);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Update failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" style={{ zIndex: 10060 }} />
      <div className="modal-container modal-sm" style={{ zIndex: 10061 }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Room charging — {label}</h2>
          <button className="modal-close" onClick={onClose} disabled={busy}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            <input type="checkbox" checked={noPost} onChange={(e) => setNoPost(e.target.checked)} />
            Room charging off — the restaurant and other outlets collect directly
          </label>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
            Applies to every room of the booking. Room and meal nights and charges posted at the desk still reach
            the folio; charges already on it stay. Use it for shared rooms of a group, where a bill cannot be traced
            to a guest.
            {!folioId && ' It is applied as the guests are checked in.'}
          </div>
          <label className="form-label">Credit limit</label>
          <input type="number" className="form-input" value={limit} min={0} step={100}
            placeholder="No limit" onChange={(e) => setLimit(e.target.value)} />
          <label className="form-label" style={{ marginTop: '0.75rem' }}>Reason <span className="text-danger">*</span></label>
          <input type="text" className="form-input" value={reason} maxLength={200}
            placeholder="Shown to the desk and kept in the audit" onChange={(e) => setReason(e.target.value)} />
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={() => { void save(); }}
            disabled={busy || reason.trim().length < 3 || (limit.trim() !== '' && Number(limit) < 0)}>
            {busy ? 'Saving…' : folioId ? 'Save' : 'Apply'}
          </button>
        </div>
      </div>
    </>
  );
};

export default RoomChargingModal;
